<script lang="ts" setup>
  import { DateTime } from 'luxon';

  import type { IChangeLogWhatsNewFeature, IChangeLogWhatsNewTickets } from '@/interfaces/agency/change-log';
  import { useChangeLogStore } from '@/stores/agency/change-log';

  defineOptions({ inheritAttrs: false });

  const SECTION_META: Record<string, { label: string; color: string; icon: string }> = {
    Features: { label: 'Features', color: 'success', icon: 'tabler-star' },
    Improvements: { label: 'Improvements', color: 'info', icon: 'tabler-settings' },
    Fixes: { label: 'Fixes', color: 'warning', icon: 'tabler-alarm' }
  };

  const changeLogStore = useChangeLogStore();

  const changeLogWhatsNew = computed(() => changeLogStore.getChangeLogLast7days);

  const buildSections = (tickets?: IChangeLogWhatsNewTickets) =>
    (['Features', 'Improvements', 'Fixes'] as const)
      .map(key => ({ key, ...SECTION_META[key], items: tickets?.[key] ?? [] }))
      .filter(section => section.items.length > 0);

  const formatReleaseDate = (releaseDate?: string) => {
    if (!releaseDate) {
      return '';
    }

    const parsed = DateTime.fromISO(releaseDate);

    return parsed.isValid ? parsed.toFormat('dd LLL yyyy') : releaseDate;
  };

  const releases = computed(() =>
    changeLogWhatsNew.value
      .map(release => ({
        version: release.version,
        date: formatReleaseDate(release.releaseDate),
        sections: buildSections(release.tickets)
      }))
      .filter(release => release.sections.length > 0)
  );

  const hasUpdates = computed(() => releases.value.length > 0);

  const itemTitle = (item: IChangeLogWhatsNewFeature) => item.alternateTitle || item.summary;

  const isDrawerVisible = ref(false);

  const displayCount = computed(() =>
    releases.value.reduce(
      (total, release) => total + release.sections.reduce((sum, section) => sum + section.items.length, 0),
      0
    )
  );

  onMounted(() => {
    const selectedDate = DateTime.now();
    const currentDate = selectedDate.toFormat('yyyy-MM-dd');
    const startDate = selectedDate.minus({ days: 6 }).toFormat('yyyy-MM-dd');

    changeLogStore.fetchChangeLogLast7days(startDate, currentDate);
  });
</script>

<template>
  <IconBtn v-if="displayCount > 0" id="change-log-btn" v-bind="$attrs" @click="isDrawerVisible = true">
    <VBadge color="error" :content="displayCount" class="change-log-badge">
      <VIcon size="26" icon="tabler-speakerphone" />
    </VBadge>
  </IconBtn>

  <VDialog
    :width="520"
    :model-value="isDrawerVisible"
    persistent
    height="100%"
    class="setting-service-drawer"
    transition="dialog-right-transition"
    no-click-animation
    @update:model-value="isDrawerVisible = false"
  >
    <VCard class="flex-grow-1 min-h-0 rounded-0 change-log-card">
      <div class="change-log-header pa-4 bg-primary color-on-primary">
        <div class="d-flex align-center justify-space-between">
          <div class="d-flex align-center ga-2">
            <VIcon icon="tabler-confetti" size="22" />
            <span class="text-h6 text-white font-weight-bold">New this week</span>
          </div>

          <IconBtn size="small" variant="text" @click="isDrawerVisible = false">
            <VIcon icon="tabler-x" size="18" />
          </IconBtn>
        </div>

        <div class="change-log-subtitle text-body-2 text-white mt-1">Updates from the last 7 days</div>
      </div>

      <VDivider />

      <div class="h-100 flex-grow-1 change-log-body py-4 overflow-y-auto">
        <template v-for="release in releases" :key="release.version">
          <div class="px-4 py-2 px-1 d-flex align-center ga-2">
            <VChip size="small" color="success" variant="tonal" label>{{ release.version }}</VChip>
            <span class="text-body-2 text-medium-emphasis">{{ release.date }}</span>
          </div>

          <div v-for="section in release.sections" :key="section.key" class="px-4 change-log-section mb-4">
            <div class="d-flex align-center ga-1" :class="`text-${section.color}`">
              <VIcon :icon="section.icon" size="16" />
              <span class="text-overline font-weight-bold" :class="`text-${section.color}`">
                {{ section.label }} ({{ section.items.length }})
              </span>
            </div>

            <ul class="change-log-list">
              <li v-for="item in section.items" :key="item.issueKey" class="text-body-2 mb-2 text-on-surface">
                <div class="font-weight-medium text-dark">{{ itemTitle(item) }}</div>
                <div class="text-medium-emphasis">{{ item.changelogDescription }}</div>
              </li>
            </ul>
          </div>
        </template>

        <VCard v-show="!hasUpdates" class="text-center py-8" elevation="0" variant="tonal">
          <VCardText>
            <VIcon icon="tabler-volume-off" size="38" class="mb-3" />
            <div class="text-body-2 text-medium-emphasis">No Updates Found!</div>
          </VCardText>
        </VCard>
      </div>

      <VDivider />

      <VCardText class="justify-end">
        <VBtn color="primary" variant="text" block to="/agency/change-log" @click="isDrawerVisible = false">
          View full Change Log
          <VIcon icon="tabler-arrow-right" size="18" class="ms-1" />
        </VBtn>
      </VCardText>
    </VCard>
  </VDialog>
</template>

<style scoped lang="scss">
  .change-log-card {
    .change-log-section {
      &:last-child {
        margin-block-end: 0 !important;
      }
    }

    .change-log-list {
      margin: 0;
      padding-inline-start: 24px;
    }
  }
</style>
